import type { AdminCardProps } from '~/components/workbench/admin-extensions/registry';
import { apiReviewSummary } from '~/lib/api/reviews';
import { apiGetProduct } from '~/lib/api/shop';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import { BTN_GHOST, CAPTION, CARD } from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';

/**
 * First-party admin card on shop.product.details: the product's rating
 * summary from the Reviews plugin (exact, from the summary endpoint), with a
 * link to moderate in the Reviews panel.
 */
export function ProductReviewsCard({ appId, resourceId }: AdminCardProps) {
  const summary = useResource(
    resourceId ? async () => apiReviewSummary(appId, (await apiGetProduct(appId, resourceId)).product.slug) : null,
    `${appId}:${resourceId ?? ''}`,
  );
  const data = summary.data;

  // Reviews not enabled / not reachable: stay out of the way.
  if (!resourceId || summary.error) {
    return null;
  }

  return (
    <section className={classNames(CARD, 'flex items-center gap-3 px-4 py-3')} aria-label="Reviews">
      <div className="i-ph:star-fill text-[15px] text-warning" />
      <div className="min-w-0 flex-1">
        <div className={CAPTION}>Reviews</div>
        <div className="mt-0.5 text-[13px] text-primary">
          {!data ? (
            <span className="text-muted">Loading…</span>
          ) : data.average === null ? (
            <span className="text-muted">No approved reviews yet</span>
          ) : (
            <>
              <span className="font-medium">{data.average.toFixed(1)}</span>
              <span className="text-muted"> / 5 · {data.count === 1 ? '1 review' : `${data.count} reviews`}</span>
            </>
          )}
          {data && data.pending > 0 && <span className="text-warning"> · {data.pending} waiting</span>}
        </div>
      </div>
      <button type="button" className={BTN_GHOST} onClick={() => workbenchStore.openTool('reviews')}>
        Open Reviews
        <div className="i-ph:arrow-right text-[12px]" />
      </button>
    </section>
  );
}
